import React from 'react';
import { Link } from 'react-router-dom';
import './Home.css';

const WHAT_IS_IMAGE = '/images/home/webp/home-poppygrid.webp';
const PROBLEM_IMAGE = '/images/home/webp/home-problem.webp';

const HOW_IT_WORKS = [
  {
    id: 'hotspot',
    title: 'Hotspot',
    description: 'Turn a physical location into an interactive environment.',
    image: '/images/home/webp/home-hotspots.webp',
  },
  {
    id: 'groups',
    title: 'Groups',
    description: 'Create experiences around them.',
    image: '/images/home/webp/home-groups.webp',
  },
  {
    id: 'broadcasts',
    title: 'Broadcasts',
    description: 'Reach people nearby and encourage participation.',
    image: '/images/home/webp/home-broadcast.webp',
  },
];

const REAL_SPACES = [
  { id: 'cafe', label: 'Café', image: '/images/home/webp/home-cafe.webp' },
  { id: 'coworking', label: 'Co-working', image: '/images/home/webp/home-co-working.webp' },
  { id: 'university', label: 'University', image: '/images/home/webp/home-university.webp' },
  { id: 'events', label: 'Events', image: '/images/home/webp/home-events.webp' },
  { id: 'communities', label: 'Communities', image: '/images/home/webp/home-community.webp' },
];

const WHY_IT_MATTERS = [
  {
    title: 'More engagement',
    description: 'Create opportunities and interactions.',
  },
  {
    title: 'Strong community',
    description: 'Help people feel part of something.',
  },
  {
    title: 'More memorable experiences',
    description: 'Turn visits into experiences.',
  },
];

const HomeImage = ({ src, alt, className, placeholderLabel }) => {
  const [failed, setFailed] = React.useState(!src);

  if (failed || !src) {
    return (
      <div className={`home-image-placeholder ${className || ''}`} aria-hidden={!alt}>
        <span>{placeholderLabel}</span>
        <p>Drop image in <code>public/images/home/</code></p>
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={alt}
      className={className}
      onError={() => setFailed(true)}
    />
  );
};

const Home = () => {
  return (
    <div className="home-container">
      {/* Section 1 — What is PoppyGrid */}
      <section className="home-section home-what" aria-labelledby="what-heading">
        <div className="home-section-inner home-split">
          <div className="home-split-text">
            <p className="home-eyebrow">What is PoppyGrid</p>
            <h1 id="what-heading" className="home-headline">
              Turn any space into a place where people connect.
            </h1>
            <p className="home-subheading">
              PoppyGrid adds an interactive layer to physical spaces, helping people discover, connect, and engage through shared moments.
            </p>
          </div>
          <div className="home-split-media">
            <HomeImage
              src={WHAT_IS_IMAGE}
              alt="People connecting in a physical space"
              className="home-image"
              placeholderLabel="What is PoppyGrid"
            />
          </div>
        </div>
      </section>

      {/* Section 2 — Problem */}
      <section className="home-section home-problem" aria-labelledby="problem-heading">
        <div className="home-section-inner">
          <h2 id="problem-heading" className="home-headline home-headline--center">
            People are surrounded by others, yet connection feels out of reach.
          </h2>
          <p className="home-body home-body--center">
            Whether it's a café, co-working space, community, or event, people often share the same space, interests, and moments, but rarely interact.
          </p>
          <blockquote className="home-pullquote">
            <p>The opportunity is here.</p>
            <p className="home-pullquote-emphasis">The connection isn't.</p>
          </blockquote>
        </div>
        <div className="home-fullbleed-media">
          <HomeImage
            src={PROBLEM_IMAGE}
            alt="Shared spaces where connection is missing"
            className="home-image home-image--full"
            placeholderLabel="Problem"
          />
        </div>
      </section>

      {/* Section 3 — Solution */}
      <section className="home-section home-solution" aria-labelledby="solution-heading">
        <div className="home-section-inner">
          <p className="home-eyebrow home-eyebrow--center">Introducing PoppyGrid</p>
          <h2 id="solution-heading" className="home-headline home-headline--center">
            A social layer for physical space.
          </h2>
          <p className="home-body home-body--center home-body--narrow">
            PoppyGrid helps spaces become more interactive. From a room to your city, it's a simple way to discover and engage with other people around you.
          </p>
          <ul className="home-benefits">
            <li className="home-benefit">
              <span className="home-benefit-label">No networking events</span>
              <span className="home-benefit-detail">Connection happens in the flow of real life, not on a calendar.</span>
            </li>
            <li className="home-benefit">
              <span className="home-benefit-label">No awkward introductions</span>
              <span className="home-benefit-detail">Shared context in the space does the heavy lifting.</span>
            </li>
            <li className="home-benefit">
              <span className="home-benefit-label">Just moments that feel natural</span>
              <span className="home-benefit-detail">Discover and engage when it actually makes sense.</span>
            </li>
          </ul>
        </div>
      </section>

      {/* Section 4 — How it works */}
      <section className="home-section home-how" aria-labelledby="how-heading">
        <div className="home-section-inner">
          <h2 id="how-heading" className="home-headline home-headline--center">
            How it works
          </h2>
          <div className="home-how-grid">
            {HOW_IT_WORKS.map((item) => (
              <article key={item.id} className="home-card home-how-card">
                <div className="home-how-card-media">
                  <HomeImage
                    src={item.image}
                    alt={item.title}
                    className="home-image home-image--card"
                    placeholderLabel={item.title}
                  />
                </div>
                <h3 className="home-card-title">{item.title}</h3>
                <p className="home-card-text">{item.description}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Section 5 — Real spaces */}
      <section className="home-section home-spaces" aria-labelledby="spaces-heading">
        <div className="home-section-inner">
          <h2 id="spaces-heading" className="home-headline home-headline--center">
            Designed for spaces that bring people together.
          </h2>
          <div className="home-spaces-grid">
            {REAL_SPACES.map((space) => (
              <article key={space.id} className="home-card home-space-card">
                <div className="home-space-card-media">
                  {space.image ? (
                    <HomeImage
                      src={space.image}
                      alt={space.label}
                      className="home-image home-image--space"
                      placeholderLabel={space.label}
                    />
                  ) : (
                    <div className="home-image-placeholder home-image--space" aria-hidden="true">
                      <span>{space.label}</span>
                    </div>
                  )}
                </div>
                <p className="home-space-label">{space.label}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Section 6 — Why it matters */}
      <section className="home-section home-why" aria-labelledby="why-heading">
        <div className="home-section-inner">
          <h2 id="why-heading" className="home-headline home-headline--center">
            Spaces are remembered for what happens inside them.
          </h2>
          <div className="home-why-grid">
            {WHY_IT_MATTERS.map((item) => (
              <article key={item.title} className="home-card home-why-card">
                <h3 className="home-card-title">{item.title}</h3>
                <p className="home-card-text">{item.description}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Section 7 — CTA */}
      <section className="home-section home-cta" aria-labelledby="cta-heading">
        <div className="home-section-inner home-cta-inner">
          <h2 id="cta-heading" className="home-headline home-headline--center">
            Ready to make your space more engaging?
          </h2>
          <p className="home-body home-body--center home-body--narrow">
            See how PoppyGrid can help create more meaningful interaction in your space.
          </p>
          <div className="home-cta-actions">
            <Link to="/contact" className="home-btn home-btn--primary">
              Book a Demo
            </Link>
            <Link to="/contact" className="home-btn home-btn--secondary">
              Contact us
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
